import {BillingPlanPanel} from '../billing-page/billing-plan-panel';
import {Trans} from '@ui/i18n/trans';
import {useProducts} from './requests/use-products';
import {Skeleton} from '@ui/skeleton/skeleton';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {Button} from '@ui/buttons/button';
import {ProductCard} from './product-card';
import {useCartStore} from './use-cart-store';
import {ShoppingCartIcon} from '@ui/icons/material/ShoppingCart';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {CartDialog} from './cart-dialog';
import {useNavigate} from '@common/ui/navigation/use-navigate';

export function ShopPage() {
  const query = useProducts();
  const cart = useCartStore((state: any) => state.cart);
  const navigate = useNavigate();

  // Determine currency based on products - use product's localCurrency or default to GBP
  const products = query.data?.products || [];
  const firstProductWithCurrency = products.find(p => p.localCurrency);
  const localCurrency = firstProductWithCurrency?.localCurrency || "GBP";
  const localCurrencyRate = 1; // Example conversion rate from GBP/USD to INR

  const cartCount = cart.reduce((count: number, item: any) => count + item.quantity, 0);

  if (query.isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50 px-16">
        <div className="flex flex-col items-center gap-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          <p className="text-muted text-sm md:text-base">
            <Trans message="Loading products..." />
          </p>
        </div>
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="container mx-auto px-16 md:px-24 py-16 md:py-24 max-w-1280">
        <BillingPlanPanel title={<Trans message="Shop" />}>
          <div className="text-center py-16 md:py-32 text-danger">
            <Trans message="Failed to load products. Please try again later." />
          </div>
        </BillingPlanPanel>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-16 md:px-24 py-16 md:py-24 max-w-1280">
      <div className="mb-32 md:mb-64">
        {/* Custom Header with Title and Cart */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-16 pb-16 mb-16 border-b border-divider">
          <div className="text-lg md:text-sm font-medium uppercase">
            <Trans message="Shop - License Keys" />
          </div>
          {cartCount > 0 && (
            <DialogTrigger type="modal">
              <Button 
                variant="outline" 
                startIcon={<ShoppingCartIcon />}
                size="sm"
                className="w-full sm:w-auto"
              >
                <Trans message="Cart" /> ({cartCount})
              </Button>
              <CartDialog 
                localCurrency={localCurrency}
                localCurrencyRate={localCurrencyRate}
              />
            </DialogTrigger>
          )}
        </div>

        {/* Products Content */}
        <div>
          <AnimatePresence initial={false} mode="wait">
            {products.length === 0 ? (
              <m.div
                {...opacityAnimation}
                className="text-center py-16 md:py-32 text-muted"
              >
                <Trans message="No products available at this time" />
              </m.div>
            ) : (
              <m.div {...opacityAnimation} className="space-y-12 md:space-y-16">
                {products.map(product => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    localCurrency={localCurrency}
                    localCurrencyRate={localCurrencyRate}
                  />
                ))}
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
